import * as React from 'react';
import { getIntrinsicElementProps, slot, useId } from '@fluentui/react-components';
import { useAnnounce_unstable as useAnnounceContext } from '@fluentui/react-shared-contexts';
import type { SystemMessageProps, SystemMessageState } from './SystemMessage.types';
import { useCopilotMode } from '@fluentui-copilot/react-provider';
import { useMessageAttributes } from '../../utils/useMessageAttributes';

// If you add JSX to this file, be sure to change the file type to .tsx

/**
 * Create the state required to render SystemMessage.
 *
 * The returned state can be modified with hooks such as useSystemMessageStyles_unstable,
 * before being passed to renderSystemMessage_unstable.
 *
 * @param props - props from this instance of SystemMessage
 * @param ref - reference to root HTMLElement of SystemMessage
 */
export const useSystemMessage_unstable = (
  props: SystemMessageProps,
  ref: React.Ref<HTMLDivElement>,
): SystemMessageState => {
  const { announcement, defaultFocused = false } = props;

  const messageId = useId('system-message-', props.id);

  const { announce } = useAnnounceContext();

  React.useEffect(() => {
    announcement && announce(announcement, { batchId: messageId });
  }, [announce, announcement, messageId]);

  const mode = useCopilotMode(props.mode);

  const { id: _, ...copilotChatAttributes } = useMessageAttributes({ id: messageId, defaultFocused });

  return {
    mode,
    components: {
      root: 'div',
      icon: 'span',
      message: 'span',
      accessibleHeading: 'h6',
    },
    root: slot.always(
      getIntrinsicElementProps('div', {
        ref,
        // If we have an icon, include it in the focus rect
        ...(props.icon ? copilotChatAttributes : {}),
        ...props,
      }),
      { elementType: 'div' },
    ),
    icon: slot.optional(props.icon, {
      renderByDefault: true,
      elementType: 'span',
    }),
    message: slot.optional(props.message, {
      defaultProps: {
        id: messageId,
        // If we don't have an icon, only show the focus rect around the message
        ...(props.icon ? {} : copilotChatAttributes),
      },
      renderByDefault: true,
      elementType: 'span',
    }),
    accessibleHeading: slot.optional(props.accessibleHeading, {
      defaultProps: {
        children: 'System message: ',
      },
      elementType: 'h6',
      renderByDefault: true,
    }),
  };
};
